/**
 * useRatingStats Hook
 * 
 * Fetches and returns rating statistics for a persona.
 * Used for filter unlock logic and progress tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

function useRatingStats() {
    const { activePersona, getAuthHeaders } = useAuth();

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        if (!activePersona) {
            setStats(null);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/ratings/${activePersona.id}`, { headers });

            if (!response.ok) {
                throw new Error('Failed to fetch rating stats');
            }

            const data = await response.json();
            setStats(data.stats || {
                total: 0,
                enthusiastic: 0,
                curious: 0,
                neutral: 0,
                hesitant: 0,
                hard_limit: 0
            });
        } catch (err) {
            console.error('Error fetching rating stats:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [activePersona, getAuthHeaders]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Unlock thresholds
    const unlockStatus = {
        basicFilters: (stats?.total || 0) >= 10,      // Category, intensity filters
        advancedFilters: (stats?.total || 0) >= 25,   // Experience, context filters
        recommendations: (stats?.total || 0) >= 50,   // "For You" algorithm
        compatibility: (stats?.total || 0) >= 100     // Partner compatibility matching
    };

    return {
        stats,
        loading,
        error,
        refetch: fetchStats,
        totalRatings: stats?.total || 0,
        ...unlockStatus
    };
}

export default useRatingStats;
